import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { FilterCondition } from "epsm-api-types";
import {
	SCREENER_COLUMNS,
	SCREENER_GROUPS,
	type ScreenerSubField,
} from "epsm-api-types/models";
import { ClipLoader } from "react-spinners";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import { useWidgetView } from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import { ONE_DAY_IN_MS } from "@/lib/utils";
import { KEY } from "@/shared/constants/keyboard";
import sprites from "/src/shared/assets/sprite.svg";
import { formatValue } from "../../helpers/FormattingHelpers";
import { isRelativeDate } from "../lib/helpers/isRelativeDate";

export const Results = ({
	refetch,
}: {
	refetch: UseInfiniteQueryResult["refetch"];
}) => {
	const {
		selectedColumn,
		setSelectedColumn,
		INITIAL_GROUP,
		selectedGroup,
		setSelectedGroup,
		setIsRelative,
	} = useWidgetView();

	const {
		filterData,
		results,
		setResults,
		setValue,
		resultsRef,
		setListOptions,
		getOptions,
		setField,
		getProcessedValue,
		setSelectedSubField,
		isResultSelectedRef,
		getSubFieldName,
		setNeedsRecalculation,
		isTotalCountLoading,
	} = useFilterData() as {
		results: Array<{
			result: Array<{
				name: string;
				condition: FilterCondition;
				value: unknown;
			}>;
			totalCount: number;
		}>;
	} & FilterDataContextProps;

	const foundColumn = (resultName: string) => {
		const nameParts = resultName.split(".");

		return SCREENER_COLUMNS.find(
			(column) =>
				column.name === (nameParts.length < 2 ? resultName : nameParts[0]),
		);
	};

	const foundGroup = (resultName: string) => {
		return SCREENER_GROUPS.find(
			(group) => group.name === foundColumn(resultName)?.groupName,
		);
	};

	const foundSubField = (resultName: string): Option | null => {
		const findNestedSubField = (
			subFields: ScreenerSubField[] | undefined,
			pathParts: string[],
			parentPath = "",
			parentTitle = "",
		): { value: string; label: string } | null => {
			if (!subFields || pathParts.length === 0) {
				return null;
			}

			const [currentPart, ...remainingParts] = pathParts;

			const currentSubField = subFields.find((sub) => sub.name === currentPart);

			if (!currentSubField) {
				return null;
			}

			const currentTitle =
				currentSubField.titleShort ||
				currentSubField.title ||
				currentSubField.name;
			const fullTitle = parentTitle
				? `${parentTitle} (${currentTitle})`
				: currentTitle;
			const fullPath = parentPath
				? `${parentPath}.${currentSubField.name}`
				: currentSubField.name;

			if (remainingParts.length) {
				return findNestedSubField(
					currentSubField.subFields,
					remainingParts,
					fullPath,
					fullTitle,
				);
			}

			return {
				value: fullPath,
				label: fullTitle,
			};
		};

		const pathParts = resultName.split(".");

		const subField = findNestedSubField(
			foundColumn(resultName)?.subFields,
			pathParts.slice(1),
		);

		return subField || null;
	};

	const convertCondition = (condition: string) => {
		switch (condition) {
			case "gt":
			case "lt":
				return "<";
			default:
				return "≤";
		}
	};

	const handleResultSelection = (
		result: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
	) => {
		isResultSelectedRef.current = true;

		const firstResultName = result[0].name;
		const isDate = foundColumn(firstResultName)?.type === "date";

		const resultValue = String(result[0]?.value);

		setIsRelative(isDate && isRelativeDate(resultValue));

		if (
			result[0].name === selectedColumn.Screener?.name ||
			result[0].name === getSubFieldName()
		) {
			return;
		}

		setSelectedColumn({
			...selectedColumn,
			Screener: foundColumn(result[0].name) || null,
		});

		if (selectedGroup.Screener !== INITIAL_GROUP) {
			setSelectedGroup({
				...selectedGroup,
				Screener: foundGroup(result[0].name)?.name || INITIAL_GROUP,
			});
		}

		setSelectedSubField(foundSubField(result[0].name));

		if (result[0].name.split(".")[0] === selectedColumn.Screener?.name) {
			isResultSelectedRef.current = false;
		}
	};

	const clear = (
		result: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
	) => {
		if (!filterData) return;

		const min = String(getProcessedValue(filterData.items[0]));
		const max = String(
			getProcessedValue(filterData.items[filterData.items.length - 1]),
		);

		if (getSubFieldName()) {
			if (result[0].name === getSubFieldName()) {
				const subField = selectedColumn.Screener?.subFields?.[0];
				setSelectedSubField({
					value: subField?.name,
					label: subField?.titleShort ?? subField?.title ?? "",
				});
				setValue({ min, max });
			}
		} else {
			setValue({ min, max });
		}

		setListOptions([]);
		setField("");
	};

	const removeResult = (
		result: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
	) => {
		const filteredResults = results.filter(
			(r) => JSON.stringify(r.result) !== JSON.stringify(result),
		);

		setResults(filteredResults);
		resultsRef.current = filteredResults;
		refetch();
		clear(result);
		setNeedsRecalculation(true);
	};

	const getFormattedText = (
		results: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}>,
	) => {
		let text = "";

		const firstResultName = results[0].name;
		const column = foundColumn(firstResultName);

		const getNestedSubfieldPath = (
			subfields: ScreenerSubField[] | undefined,
			pathParts: string[],
		): string | null => {
			if (!subfields || pathParts.length === 0) return null;

			const [currentPart, ...remainingParts] = pathParts;

			const subfield = subfields.find((sub) => sub.name === currentPart);
			if (!subfield) return null;

			if (remainingParts.length > 0) {
				const childPath = getNestedSubfieldPath(
					subfield.subFields,
					remainingParts,
				);
				return childPath
					? `${subfield.titleShort || subfield.title || subfield.name} > ${childPath}`
					: subfield.titleShort || subfield.title || subfield.name;
			}

			return subfield.titleShort || subfield.title || subfield.name;
		};

		const pathParts = firstResultName.split(".");
		const columnTitle =
			column?.titleAbbreviation ?? column?.titleShort ?? column?.title;
		const subFieldPath = getNestedSubfieldPath(
			column?.subFields,
			pathParts.slice(1),
		);

		const title = subFieldPath
			? `${columnTitle} (${subFieldPath})`
			: columnTitle;

		const isDate = foundColumn(firstResultName)?.type === "date";

		const getFormattedValue = (value: unknown) => {
			if (!column) return;

			if (isDate && isRelativeDate(value))
				return Math.round(Number(value) / ONE_DAY_IN_MS);

			return formatValue(value, column.type, column.options);
		};

		const newResults = results.map((result) => {
			return {
				...result,
				condition:
					isDate && ["lt", "lte"].includes(result.condition)
						? "lte"
						: result.condition,
				value: getFormattedValue(result.value),
			};
		});

		if (column?.view === "list") {
			text = `${title}: ${(results[0].value as unknown[])
				.map((item) => {
					return (
						column.options?.[item as keyof typeof column.options] ??
						(getOptions(item, column)?.label || "")
					);
				})
				.join(", ")}`;
		} else {
			switch (column?.type) {
				case "num":
				case "date":
					text = ["lt", "lte"].includes(newResults[0].condition)
						? `${title} ${convertCondition(newResults[0].condition)} ${newResults[0].value}`
						: `${newResults[0].value} ${convertCondition(newResults[0].condition)} ${title}`;

					if (newResults[1]) {
						text += ` ${convertCondition(newResults[1].condition)} ${newResults[1].value}`;
					}
					break;

				case "str":
					text = `${title} include ${results[0].value}`;
					break;
				case "bool":
					if (column) {
						const values = Array.isArray(results[0].value)
							? results[0].value
							: [results[0].value];

						text = `${title}: ${values
							.map((item) => getOptions(item, column)?.label || "")
							.join(", ")}`;
					}
			}
		}

		return text;
	};

	return (
		<div className="relative min-w-40 w-2/4 border-box rounded-sm">
			<h3 className="py-1 px-1.5 text-accent-foreground italic">Results</h3>

			<div className="overflow-y-scroll max-h-without-btn">
				<div className="flex-col items-center gap-2 px-1.5">
					{results.map((result) => (
						<div
							key={result.result[0].name}
							className="flex items-center gap-2 mb-0.5"
						>
							<Tooltip>
								<TooltipTrigger asChild>
									<span
										className="flex justify-between hover:shadow-none min-w-5 w-full h-max 
										p-0.5 text-[var(--light-gray)] hover:text-accent-foreground"
										onClick={() => handleResultSelection(result.result)}
										onKeyDown={(event) => {
											if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
												event.preventDefault();
												handleResultSelection(result.result);
											}
										}}
									>
										<div className="text-ellipsis overflow-hidden whitespace-nowrap">
											{getFormattedText(result.result)}
										</div>

										<Button
											className="h-max py-1 px-1 shadow-none group bg-transparent"
											onClick={() => removeResult(result.result)}
										>
											<svg
												aria-label="close"
												role="img"
												fill="none"
												stroke="var(--light-gray)"
												className="w-3 h-3 group-hover:stroke-accent-foreground"
											>
												<use xlinkHref={`${sprites}#close`} />
											</svg>
										</Button>
									</span>
								</TooltipTrigger>

								<TooltipContent
									side="top"
									align="end"
									className="translate-x-[-90px] custom-fade-in"
								>
									{getFormattedText(result.result)}
								</TooltipContent>
							</Tooltip>

							{isTotalCountLoading ? (
								<ClipLoader size={12} color="var(--gray)" />
							) : (
								<p className="w-1/6">{result.totalCount}</p>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};
